import { constants } from '@eti/utils';
import { checkInBaggage } from '@eti/products';
import { useTranslation } from '@eti/providers';
import {
  resolveBaggageForTraveler,
  resolveTravelerLabel,
  resolveSecondBaggageForTraveler,
} from '../../../widgets/cart/utils/baggage';
import type {
  TransformedTDCartProductType,
  TransformedCartTravelerPriceType,
} from '../../../common/types/transformations/models';
import useNameFormatter from '../../../common/custom-hooks/useNameFormatter';

const { INFANT } = constants.ageTypes;

const getCheckInBaggageSelection = (addedProducts: TransformedTDCartProductType[]) =>
  addedProducts.find((product) => product.id === checkInBaggage.id) ?? null;

const useSelectedCheckInBaggage = (
  travelers: TransformedCartTravelerPriceType[],
  products: TransformedTDCartProductType[],
) => {
  const { t } = useTranslation();
  const notSelectedLabel = t('Cart.Baggage.Placeholder');
  const { formatName } = useNameFormatter();
  const baggageSelection = getCheckInBaggageSelection(products);

  if (!baggageSelection) {
    return [{ label: notSelectedLabel, noBaggageSelected: true }];
  }

  const baggageProductId = baggageSelection.id;
  return travelers.map((traveler) => {
    const baggageSelectionForTraveler = baggageSelection.travelers.find(
      (selection) => selection.travelerId === traveler.travelerId,
    );

    if (baggageSelectionForTraveler) {
      if (baggageProductId === checkInBaggage.id) {
        const { numberOfPieces: pieces, weight, weightUnit } = baggageSelectionForTraveler;
        return {
          label: resolveTravelerLabel(
            t,
            traveler.travelerId,
            formatName(traveler.firstName, traveler.lastName, traveler.fullName),
          ),
          value: resolveBaggageForTraveler(t, { pieces, weight, weightUnit }),
          secondBagValue:
            pieces > 1 ? null : resolveSecondBaggageForTraveler(t, baggageSelectionForTraveler),
          noBaggageSelected: undefined,
        };
      }
    }

    if (traveler.ageType === INFANT) {
      return {
        label: resolveTravelerLabel(
          t,
          traveler.travelerId,
          formatName(traveler.firstName, traveler.lastName, traveler.fullName),
        ),
        value: t('Cart.Baggage.ContactAirline'),
        noBaggageSelected: undefined,
      };
    }

    return {
      label: resolveTravelerLabel(
        t,
        traveler.travelerId,
        formatName(traveler.firstName, traveler.lastName, traveler.fullName),
      ),
      value: notSelectedLabel,
      noBaggageSelected: undefined,
    };
  });
};

export const useBookingStepSelectedCheckInBaggage = (
  travelers: TransformedCartTravelerPriceType[],
  products: TransformedTDCartProductType[],
) => {
  const { t } = useTranslation();
  const notSelectedLabel = t('Cart.Baggage.Placeholder');
  const { formatName } = useNameFormatter();
  const baggageSelection = getCheckInBaggageSelection(products);

  if (!baggageSelection) {
    return [{ label: notSelectedLabel, noBaggageSelected: true }];
  }

  const baggageProductId = baggageSelection.id;
  return travelers.map((traveler) => {
    const baggageSelectionForTraveler = baggageSelection.travelers.find(
      (selection) => selection.travelerId === traveler.travelerId,
    );

    if (baggageSelectionForTraveler) {
      if (baggageProductId === checkInBaggage.id) {
        const {
          numberOfUnits: pieces,
          maxWeight: weight,
          weightUnit,
        } = baggageSelectionForTraveler;
        return {
          label: resolveTravelerLabel(
            t,
            traveler.travelerId,
            formatName(traveler.firstName, traveler.lastName, traveler.fullName),
          ),
          value: resolveBaggageForTraveler(t, { pieces, weight, weightUnit }),
          secondBagValue:
            pieces > 1 ? null : resolveSecondBaggageForTraveler(t, baggageSelectionForTraveler),
          noBaggageSelected: undefined,
        };
      }
    }

    if (traveler.ageType === INFANT) {
      return {
        label: resolveTravelerLabel(
          t,
          traveler.travelerId,
          formatName(traveler.firstName, traveler.lastName, traveler.fullName),
        ),
        value: t('Cart.Baggage.ContactAirline'),
        noBaggageSelected: undefined,
      };
    }

    return {
      label: resolveTravelerLabel(
        t,
        traveler.travelerId,
        formatName(traveler.firstName, traveler.lastName, traveler.fullName),
      ),
      value: notSelectedLabel,
      noBaggageSelected: undefined,
    };
  });
};

export default useSelectedCheckInBaggage;
